// A small shield on an avatar's corner (Instagram "verified"-style placement):
// green = the vendor holds a verified second factor, muted slashed = none.
//
// Purely presentational — the caller supplies `enabled` from the batched
// status query (apps/admin/src/lib/useMfaStatuses.ts). Nothing here fetches,
// because the Vendors list renders up to 200 of these and a badge that fetched
// would be 200 round trips (spec D9).
//
// Renders NOTHING while the status is unknown, deliberately. The rows arrive
// before the status call resolves, so defaulting to "off" would show a wrong
// badge on every enrolled vendor for a beat, then flip. The parent element
// must be position-relative.
// `enabled?: boolean | undefined` rather than `enabled?: boolean`: the repo
// runs exactOptionalPropertyTypes, and the caller's Map.get(id) yields an
// explicit undefined for a vendor whose status has not arrived — which is
// precisely the case this component exists to render as nothing.
export function MfaBadge({
  enabled,
  size = 'lg',
}: {
  enabled?: boolean | undefined;
  size?: 'sm' | 'lg' | undefined;
}) {
  if (enabled == null) return null;

  const label = enabled ? 'Two-factor authentication enabled' : 'No two-factor authentication';
  const box = size === 'sm' ? 'h-3.5 w-3.5 -bottom-0.5 -right-0.5' : 'h-5 w-5 -bottom-1 -right-1';
  const glyph = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <span
      title={label}
      aria-label={label}
      className={
        `absolute ${box} flex items-center justify-center rounded-full ring-2 ring-white ` +
        (enabled ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-600')
      }
    >
      {enabled ? <ShieldCheckIcon className={glyph} /> : <ShieldOffIcon className={glyph} />}
    </span>
  );
}

function ShieldCheckIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function ShieldOffIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="M4.5 4.5l15 15" />
    </svg>
  );
}
